import React, { useEffect, useState, useMemo } from 'react';
import { useParams, Link, Navigate } from 'react-router-dom';
import { ChevronRight, Star, MapPin, Award, MessageCircle, Sparkles } from 'lucide-react';
import { Button } from '../components/ui/button';
import { CITIES, CATEGORIES } from '../mock/mock';
import LawFirmCard from '../components/LawFirmCard';
import FirmsMap from '../components/FirmsMap';
import SEO from '../components/SEO';
import { firmsApi } from '../api/client';

// Long-tail SEO landing page: /advogados-de-{area}-em-{cidade}
export default function LongTailLanding() {
  const { catSlug, citySlug } = useParams();
  const cat = useMemo(() => CATEGORIES.find(c => c.slug === catSlug), [catSlug]);
  const city = useMemo(() => CITIES.find(c => c.slug === citySlug), [citySlug]);
  const [firms, setFirms] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!cat || !city) return;
    setLoading(true);
    firmsApi.list({ city: citySlug, category: catSlug, sort: 'rating', limit: 12 })
      .then(d => setFirms(d.items))
      .finally(() => setLoading(false));
  }, [catSlug, citySlug, cat, city]);

  if (!cat || !city) return <Navigate to="/advogados" replace />;

  const title = `Advogado ${cat.name} em ${city.name}`;
  const description = `Encontre os melhores advogados de ${cat.name} em ${city.name}/${city.state}. ${firms.length} escritórios verificados pela OAB, com avaliações reais e contato direto por WhatsApp.`;

  const withCoords = firms.map(f => ({ ...f, lat: city.lat, lng: city.lng }));

  const faqs = [
    { q: `Como escolher um bom advogado ${cat.name.toLowerCase()} em ${city.name}?`, a: `Verifique o registro na OAB, avaliações de clientes anteriores, experiência específica em ${cat.name.toLowerCase()} e converse pessoalmente sobre o seu caso antes de contratar.` },
    { q: `Quanto custa um advogado ${cat.name.toLowerCase()} em ${city.name}?`, a: `Os valores variam conforme a complexidade do caso. Muitos escritórios oferecem a primeira consulta gratuita. Você pode falar diretamente pelo WhatsApp com cada escritório para consultar valores.` },
    { q: `Como funciona a consulta com um advogado ${cat.name.toLowerCase()}?`, a: `Você entra em contato pelo WhatsApp ou telefone, explica seu caso e agenda uma reunião presencial ou online. O advogado avalia sua situação e propõe as melhores estratégias.` },
    { q: `Todos os escritórios listados atendem em ${city.name}?`, a: `Sim, todos os escritórios listados nesta página atendem clientes em ${city.name}/${city.state} e estão registrados na OAB local.` },
  ];

  const jsonLd = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: faqs.map(f => ({
      '@type': 'Question', name: f.q,
      acceptedAnswer: { '@type': 'Answer', text: f.a },
    })),
  };

  return (
    <div>
      <SEO title={title} description={description} jsonLd={jsonLd} />

      {/* Breadcrumb */}
      <div className="bg-white border-b border-[#cbd8dc]">
        <div className="aj-wrap py-4">
          <nav className="flex items-center gap-1 text-xs text-[#52676e] flex-wrap">
            <Link to="/" className="hover:text-[#153e44]">Início</Link><ChevronRight className="w-3 h-3" />
            <Link to="/advogados" className="hover:text-[#153e44]">Advogados</Link><ChevronRight className="w-3 h-3" />
            <Link to={`/categoria/${cat.slug}`} className="hover:text-[#153e44]">{cat.name}</Link><ChevronRight className="w-3 h-3" />
            <span>{city.name}</span>
          </nav>
        </div>
      </div>

      {/* Hero */}
      <section className="bg-white border-b border-[#cbd8dc] text-[#122f36]">
        <div className="aj-wrap py-12 md:py-16">
          <div className="inline-flex items-center gap-2 bg-[#edf4f5] border border-[#cbd8dc] rounded-full px-3 py-1 text-xs font-medium text-[#075a6d] mb-4">
            <MapPin className="w-3 h-3" /> {city.name}/{city.state}
          </div>
          <h1 className="text-3xl md:text-5xl font-semibold leading-tight">Advogado <span className="text-[#075a6d]">{cat.name}</span> em {city.name}</h1>
          <p className="mt-4 text-lg text-[#354c54] max-w-3xl leading-relaxed">Encontre os melhores escritórios especializados em <strong>{cat.name}</strong> em {city.name}/{city.state}. Todos verificados pela OAB, com avaliações reais de clientes e contato direto por WhatsApp.</p>
          <div className="flex flex-wrap gap-3 mt-6">
            <Link to="/match"><Button className="bg-[#153e44] hover:bg-[#0c3036] text-white font-semibold"><Sparkles className="w-4 h-4 mr-2" /> Match com IA</Button></Link>
            <a href="#lista"><Button variant="outline" className="bg-white border-[#2f5d65] text-[#153e44] hover:bg-[#edf4f5]">Ver escritórios</Button></a>
          </div>
        </div>
      </section>

      {/* Intro SEO content */}
      <section className="aj-wrap max-w-[820px] py-12">
        <div className="prose prose-slate max-w-none prose-headings:text-[#153e44]">
          <h2>Por que contratar um advogado {cat.name.toLowerCase()} em {city.name}?</h2>
          <p>Se você está em {city.name} e precisa resolver uma questão de <strong>{cat.name}</strong>, contar com um advogado especializado local faz toda a diferença. Além do conhecimento técnico da área, um profissional que atua na região conhece a jurisprudência dos tribunais locais, tem relação com os órgãos públicos e consegue agilizar processos.</p>
          <p>Nossa plataforma reúne {firms.length}+ escritórios verificados que atuam com {cat.name} em {city.name}/{city.state}. Todos possuem registro ativo na OAB, avaliações reais de clientes anteriores e podem ser contatados diretamente por WhatsApp — sem intermediários, sem taxas.</p>

          <h2>O que faz um advogado {cat.name.toLowerCase()}?</h2>
          <p>O advogado especializado em {cat.name} atua em questões como orientação preventiva, elaboração de contratos, defesa em processos judiciais, acordos e negociações. Ele analisa o seu caso, sugere a melhor estratégia e representa seus interesses perante o Judiciário.</p>
        </div>
      </section>

      {/* Map */}
      {withCoords.length > 0 && (
        <section className="aj-wrap pb-8">
          <h2 className="text-2xl font-semibold text-[#153e44] mb-4">Escritórios no mapa</h2>
          <FirmsMap firms={withCoords} height={400} />
        </section>
      )}

      {/* Firms list */}
      <section id="lista" className="aj-wrap pb-12">
        <div className="flex items-end justify-between mb-6">
          <h2 className="text-2xl font-semibold text-[#153e44]">Melhores escritórios de {cat.name} em {city.name}</h2>
          <Link to={`/advogados?city=${city.slug}&cat=${cat.slug}`} className="text-sm font-medium text-[#153e44] hover:text-[#063f4c]">Ver todos <ChevronRight className="w-4 h-4 inline" /></Link>
        </div>
        {loading ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {[1,2,3].map(i => <div key={i} className="bg-white rounded-[10px] border border-[#cbd8dc] h-96 animate-pulse" />)}
          </div>
        ) : firms.length === 0 ? (
          <div className="bg-white border border-[#cbd8dc] rounded-[10px] p-10 text-center text-[#52676e]">Ainda não há escritórios de {cat.name} cadastrados em {city.name}. <Link to={`/categoria/${cat.slug}`} className="text-[#153e44] font-semibold hover:text-[#063f4c]">Ver todos os escritórios de {cat.name}</Link>.</div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {firms.map(f => <LawFirmCard key={f.id} firm={f} />)}
          </div>
        )}
      </section>

      {/* FAQ */}
      <section className="bg-[#f2f6f7] py-12">
        <div className="aj-wrap max-w-[820px]">
          <h2 className="text-2xl font-semibold text-[#153e44] mb-6">Perguntas frequentes sobre advogado {cat.name.toLowerCase()} em {city.name}</h2>
          <div className="space-y-3">
            {faqs.map((f, i) => (
              <details key={i} className="group bg-white rounded-[7px] border border-[#cbd8dc] p-5 open:shadow-md">
                <summary className="font-semibold text-[#153e44] cursor-pointer list-none flex items-center justify-between">
                  {f.q}
                  <ChevronRight className="w-5 h-5 text-[#075a6d] transition-transform group-open:rotate-90" />
                </summary>
                <p className="mt-3 text-[#354c54] leading-relaxed">{f.a}</p>
              </details>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
